import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ClockIcon, PlusIcon, ShieldCheckIcon } from 'lucide-react';
import { AuthSplit } from '../../components/auth/AuthSplit';
import { Button } from '../../components/ui/Button';
import { Field, Input, inputClasses } from '../../components/ui/Field';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { experiences, experienceById } from '../../data/experiences';

/**
 * Where to, on one account.
 *
 * Shown after sign-in when more than one workspace resolves, and reachable any
 * time you want to add another experience. Two rules it exists to make visible:
 *
 *  - The list is resolved from memberships, so it can only ever contain what
 *    you actually hold. Selecting one re-checks before it moves you.
 *  - Adding an experience adds a membership. No second account, no second
 *    password, no second sign-in.
 */
export function WorkspaceSelect() {
  const navigate = useNavigate();
  const {
    identity
  } = useAuth();
  const {
    workspaces,
    memberships,
    enterWorkspace,
    addExperience
  } = useWorkspace();
  const [adding, setAdding] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const option = adding ? experienceById(adding) : null;
  const heldKinds = new Set(workspaces.map(space => space.kind));
  const waiting = memberships.filter(item => item.status === 'pending');
  const open = (id: string, path: string) => {
    const decision = enterWorkspace(id);
    if (decision.allowed) navigate(path);else setError(decision.message);
  };
  const submitAdd = (event: React.FormEvent) => {
    event.preventDefault();
    if (!option) return;
    if (option.organizationKind && orgName.trim().length < 2) {
      setError(`Give your ${option.label.toLowerCase()} a name.`);
      return;
    }
    const result = addExperience({
      experienceId: option.id,
      organizationName: orgName
    });
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(null);
    setAdding(null);
    setOrgName('');
    setNotice(result.pending ? `${option.label} added. It is waiting on confirmation, so its workspace has not opened yet.` : `${option.label} added to your account. Its workspace is open below.`);
  };
  return <AuthSplit headline={['One account.', 'Several roles.', 'No second login.']} lead="Everything you do on Campuzon belongs to the same identity — switching between them never asks you to sign in again." badge="Resolved from your memberships" data-id="element-7556" data-mp="2uueo9coy0jud">
      <h1 className="text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-tight text-ink" data-id="element-7557" data-mp="34kztvk31aoms">
        Choose your workspace
      </h1>
      <p className="mt-3 text-body text-ink-muted" data-id="element-7558" data-mp="3dkwftdy1h9e5">
        Signed in as <span className="font-semibold text-ink" data-id="element-7559" data-mp="3dmpk4ml6jhiz">@{identity?.username}</span>. These are
        the workspaces your memberships open — nothing else can be reached by asking for it.
      </p>

      {notice && <p role="status" className="mt-5 flex items-start gap-2 rounded-xl border border-ok-100 bg-ok-50 p-3 text-body-sm text-ink" data-id="element-7560" data-mp="5yajxtg7xmjs">
          <CheckIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ok-700" data-id="element-7561" data-mp="3s6a7n4vq5uo5" />
          <span data-id="element-7562" data-mp="3efwxqwz9w5w6">{notice}</span>
        </p>}
      {error && <p role="alert" className="mt-5 rounded-xl border border-danger-100 bg-danger-50 p-3 text-body-sm text-ink" data-id="element-7563" data-mp="3j6wx08vv701u">
          {error}
        </p>}

      <ul className="mt-6 space-y-3" data-id="element-7564" data-mp="34jk7ya6umc6p">
        {workspaces.map(space => <li key={space.id} data-id="element-7565" data-mp="39i58ybtxczw7">
            <button type="button" onClick={() => open(space.id, space.path)} className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-line bg-surface p-4 text-left transition-colors duration-150 ease-out hover:border-brand-200 hover:bg-brand-50" data-id="element-7566" data-mp="39hdcyzwf105e">
              <span className="min-w-0" data-id="element-7567" data-mp="2ngmwlo6s1p0d">
                <span className="block text-body font-semibold text-ink" data-id="element-7568" data-mp="2nhxx9f4xegn9">{space.label}</span>
                <span className="mt-0.5 block truncate text-caption text-ink-muted" data-id="element-7569" data-mp="2iov2j3oex15h">
                  {space.ownerLabel} · {space.roleName}
                </span>
              </span>
              <ArrowRightIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle transition-transform duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-brand-700" data-id="element-7570" data-mp="29pi1mzectkwd" />
            </button>
          </li>)}
        {workspaces.length === 0 && <li className="rounded-2xl border border-line bg-canvas p-4 text-body-sm text-ink-muted" data-id="element-7571" data-mp="1ut0ny1iozu6n">
            No workspace has opened for this account yet. Add an experience below — your account
            already exists, so this only adds a role to it.
          </li>}
      </ul>

      {waiting.length > 0 && <p className="mt-4 flex items-start gap-2 rounded-xl border border-warn-100 bg-warn-50 p-3 text-caption text-ink" data-id="element-7572" data-mp="1hcvvxiqmrx8">
          <ClockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warn-700" data-id="element-7573" data-mp="a9wxn6ubtcma" />
          <span data-id="element-7574" data-mp="abzvgd1q0a1k">
            {waiting.length} membership{waiting.length === 1 ? '' : 's'} waiting on confirmation.
            Pending memberships grant nothing, which is why their workspaces are not listed.
          </span>
        </p>}

      {/* ----------------------------------------------- add an experience */}
      <section className="mt-8 border-t border-line pt-6" data-id="element-7575" data-mp="3np2zu33slp4c">
        <h2 className="text-h4 font-semibold text-ink" data-id="element-7576" data-mp="3nni4ib9nm25f">Add an experience</h2>
        <p className="mt-2 text-body-sm text-ink-muted" data-id="element-7577" data-mp="3sopdgdnj97do">
          Adds a role to this account. You keep the same handle, the same password and the same
          session.
        </p>

        <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-7578" data-mp="17wvxfqgbf0oe">
          {experiences.map(item => {
          const held = heldKinds.has(item.id);
          const selected = adding === item.id;
          return <button key={item.id} type="button" disabled={held} onClick={() => {
            setAdding(selected ? null : item.id);
            setError(null);
          }} className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors duration-150 ease-out ${held ? 'cursor-not-allowed border-line bg-canvas' : selected ? 'border-brand-700 bg-brand-50' : 'border-line bg-surface hover:bg-canvas'}`} data-id="element-7579" data-mp="d9nd99m3zg36">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${selected ? 'bg-brand-700 text-white' : 'bg-canvas text-ink-muted'}`} data-id="element-7580" data-mp="3cxomfmmppyjv">
                  <item.icon aria-hidden className="h-[18px] w-[18px]" data-id="element-7581" data-mp="1ng188m06zv4i" />
                </span>
                <span className="min-w-0" data-id="element-7582" data-mp="1s8b5kn3mzxgt">
                  <span className="block text-body-sm font-semibold text-ink" data-id="element-7583" data-mp="1s9cd4hlg21ts">{item.label}</span>
                  <span className="block text-caption text-ink-subtle" data-id="element-7584" data-mp="1dgsglzssuslb">
                    {held ? 'Already on your account' : 'Add to this account'}
                  </span>
                </span>
                {!held && !selected && <PlusIcon aria-hidden className="ml-auto h-4 w-4 shrink-0 text-ink-subtle" data-id="element-7585" data-mp="2b6nblzz94r86" />}
              </button>;
        })}
        </div>

        {option && <form noValidate onSubmit={submitAdd} className="mt-4 space-y-4" data-id="element-7586" data-mp="217g0ljfzp8o8">
            {option.organizationKind && <Field label={`${option.label} name`} htmlFor="add-org" hint="This creates an organization you are a member of — not a second account." data-id="element-7587" data-mp="lqo1ognt6xrh">
                <Input id="add-org" value={orgName} onChange={event => setOrgName(event.target.value)} className={inputClasses} data-id="element-7588" data-mp="1hhx363od8c0o" />
              </Field>}
            <p className="flex items-start gap-2 rounded-xl border border-line bg-canvas p-3 text-caption text-ink-muted" data-id="element-7589" data-mp="13hdwazvmzc03">
              <ShieldCheckIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-7590" data-mp="205tpd5xyg3hy" />
              <span data-id="element-7591" data-mp="1w59ts6t157xx">{option.verification}</span>
            </p>
            <Button type="submit" variant="primary" data-id="element-7592" data-mp="1w7w248nqvrh7">
              Add {option.label}
            </Button>
          </form>}
      </section>
    </AuthSplit>;
}